import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ShieldCheck, Building2, Archive, Send, GitMerge } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Cross-club Demand Meter — network-wide aggregation for the Platform
 * Administrator. Pulls /api/super-admin/demand which mirrors the per-club
 * /api/admin/intelligence but without the tenant filter, plus a per-club
 * breakdown.
 */
export default function SuperAdminDemandPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/super-admin/demand")
      .then((r) => !cancelled && setData(r.data))
      .catch((err) => !cancelled && setError(formatApiErrorDetail(err.response?.data?.detail) || err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const t = data?.totals;

  return (
    <div className="bg-cream min-h-screen" data-testid="sa-demand-page">
      <header className="bg-[#0f1a14] text-[#F9F8F4] border-b border-[#1A422D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <TrendingUp size={22} className="text-[#D9C68A]" />
            <div>
              <div className="overline" style={{ color: "#D9C68A" }}>Platform</div>
              <div className="font-serif text-2xl mt-0.5">Demand Meter · Network-wide</div>
            </div>
          </div>
          <Link
            to="/super-admin"
            data-testid="sa-demand-back"
            className="inline-flex items-center gap-2 border border-[#D9C68A]/50 text-[#D9C68A] px-3 py-1.5 text-xs uppercase tracking-[0.22em] hover:bg-[#1A422D] transition-colors"
          >
            <ShieldCheck size={14} /> Back to Console
          </Link>
        </div>
      </header>

      <nav className="bg-[#142019] border-b border-[#1A422D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-3 flex items-center gap-3 flex-wrap">
          <Link to="/super-admin/clubs" data-testid="sa-nav-clubs" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#D9C68A]/80 hover:text-[#D9C68A]"><Building2 size={12} /> Clubs</Link>
          <span className="text-[#1A422D]">·</span>
          <Link to="/super-admin/demand" data-testid="sa-nav-demand" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#D9C68A]"><TrendingUp size={12} /> Demand</Link>
          <span className="text-[#1A422D]">·</span>
          <Link to="/super-admin/invitations" data-testid="sa-nav-invitations" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#D9C68A]/80 hover:text-[#D9C68A]"><Send size={12} /> Invitations</Link>
          <span className="text-[#1A422D]">·</span>
          <Link to="/super-admin/merge" data-testid="sa-nav-merge" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#D9C68A]/80 hover:text-[#D9C68A]"><GitMerge size={12} /> Merge &amp; Transfer</Link>
          <span className="text-[#1A422D]">·</span>
          <Link to="/super-admin/archive" data-testid="sa-nav-archive" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#D9C68A]/80 hover:text-[#D9C68A]"><Archive size={12} /> Archive</Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 space-y-8">
        {loading && <div className="overline text-[#7A7A7A]">Loading…</div>}
        {error && (
          <div className="px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
        )}

        {data && (
          <>
            <div>
              <div className="overline">Cross-club Demand</div>
              <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">What members across every club are asking for</h2>
              <p className="text-sm text-[#4A4A4A] mt-2 max-w-2xl">
                Aggregated across all {t?.active_clubs ?? 0} active clubs ({t?.clubs ?? 0} total).
                Generated {(data.generated_at || "").replace("T", " ").slice(0, 19)}.
              </p>
            </div>

            <section className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="sa-demand-kpis">
              <Kpi label="Active Members 30d" value={t?.active_members_30d} testId="kpi-active-30" />
              <Kpi label="Orders 30d" value={t?.orders_30d} testId="kpi-orders-30"
                   hint={t?.growth_pct_30d !== null && t?.growth_pct_30d !== undefined ? `${t.growth_pct_30d > 0 ? "+" : ""}${t.growth_pct_30d}% vs prior 30d` : "no prior data"} />
              <Kpi label="Total Members" value={t?.members} testId="kpi-members"
                   hint={`${t?.onboarded_members ?? 0} onboarded`} />
              <Kpi label="Total Orders" value={t?.orders} testId="kpi-orders" />
              <Kpi label="Wishlist (Shared)" value={t?.wishlist_shared} testId="kpi-wishlist"
                   hint={`${t?.wishlist_items ?? 0} total`} />
            </section>

            <section data-testid="sa-demand-by-club">
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
                <h3 className="font-serif text-2xl text-[#1A1A1A]">By Club</h3>
                <div className="overline">Volume contribution</div>
              </div>
              <div className="surface-card overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-[#F3F1E9]">
                    <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                      <th className="px-6 py-4">Club</th>
                      <th className="px-6 py-4 text-right">Orders</th>
                      <th className="px-6 py-4 text-right">Members</th>
                      <th className="px-6 py-4 text-right">Wishlist</th>
                      <th className="px-6 py-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.by_club || []).length === 0 && (
                      <tr><td colSpan={5} className="px-6 py-10 text-center text-[#7A7A7A]">No clubs yet.</td></tr>
                    )}
                    {(data.by_club || []).map((c) => (
                      <tr key={c.club_id} className="border-t border-[#E5E3D8]" data-testid={`sa-demand-club-${c.slug || c.club_id}`}>
                        <td className="px-6 py-4">
                          <Link to={`/super-admin/clubs/${c.club_id}`} className="font-serif text-base text-[#1A1A1A] hover:underline">
                            {c.name}
                          </Link>
                          <div className="text-xs text-[#7A7A7A] font-mono">{c.slug}</div>
                        </td>
                        <td className="px-6 py-4 text-right font-mono">{c.orders}</td>
                        <td className="px-6 py-4 text-right font-mono">{c.members}</td>
                        <td className="px-6 py-4 text-right font-mono">{c.wishlist}</td>
                        <td className="px-6 py-4 text-xs uppercase tracking-wider text-[#4A4A4A]">{c.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="grid lg:grid-cols-2 gap-6">
              <TopList title="Top Items" items={data.top_items} testId="sa-demand-top-items" />
              <TopList title="Top Brands" items={data.top_brands} testId="sa-demand-top-brands" />
              <TopList title="Top Categories" items={data.top_categories} testId="sa-demand-top-categories" />
              <TopList title="Top Sizes" items={data.top_sizes} testId="sa-demand-top-sizes" />
              <TopList title="Top Colors" items={data.top_colors} testId="sa-demand-top-colors" />
              <TopList title="Where Members See Items" items={data.top_travel_origins} testId="sa-demand-top-travel" />
              <TopList title="Top Wishlist (shared)" items={data.top_wishlist} testId="sa-demand-top-wishlist" />
              <TopList title="Favorite Brands (member-stated)" items={data.top_member_brands} testId="sa-demand-top-member-brands" />
              <TopList title="Favorite Categories (member-stated)" items={data.top_member_categories} testId="sa-demand-top-member-categories" />
              <TopList title="Style Preferences (member-stated)" items={data.top_styles} testId="sa-demand-top-styles" />
            </section>

            <section data-testid="sa-demand-velocity">
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
                <h3 className="font-serif text-2xl text-[#1A1A1A]">Submission Velocity</h3>
                <div className="overline">Last 12 ISO weeks · all clubs</div>
              </div>
              <div className="surface-card p-6">
                <VelocityChart velocity={data.submission_velocity || []} />
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, hint, testId }) {
  return (
    <div className="surface-card p-5" data-testid={testId}>
      <div className="overline">{label}</div>
      <div className="font-serif text-3xl mt-1 text-[#1A1A1A]">{value ?? "—"}</div>
      {hint && <div className="text-xs text-[#7A7A7A] mt-1.5">{hint}</div>}
    </div>
  );
}

function TopList({ title, items, testId }) {
  return (
    <div className="surface-card p-5" data-testid={testId}>
      <div className="overline">{title}</div>
      {(!items || items.length === 0) ? (
        <div className="text-xs text-[#9A9A9A] mt-3">No data yet.</div>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {items.map((it, i) => (
            <li key={`${title}-${i}-${it.label}`} className="flex items-center justify-between text-sm">
              <span className="text-[#1A1A1A] truncate pr-3">{it.label}</span>
              <span className="font-mono text-xs text-[#4A4A4A]">{it.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VelocityChart({ velocity }) {
  if (!velocity.length) return <div className="text-xs text-[#9A9A9A]">No orders in the window.</div>;
  const max = Math.max(...velocity.map((w) => w.count), 1);
  return (
    <div className="flex items-end gap-1.5 h-32">
      {velocity.map((w, i) => (
        <div
          key={`${w.year}-${w.week}-${i}`}
          className="flex-1 flex flex-col items-center gap-1 group"
          title={`Week ${w.week}, ${w.year}: ${w.count} order${w.count === 1 ? "" : "s"}`}
        >
          <div
            className="w-full bg-[#1A422D] hover:bg-[#0f3220] transition-colors"
            style={{ height: `${(w.count / max) * 100}%`, minHeight: w.count ? 4 : 1 }}
          />
          <div className="text-[10px] text-[#7A7A7A] font-mono">W{w.week}</div>
        </div>
      ))}
    </div>
  );
}
